import { Link } from "@tanstack/react-router";
import logo from "@/assets/vibing_co_logo_white.svg.asset.json";
import jonPhoto from "@/assets/profile-photo.webp.asset.json";

// The largest category hubs, linked sitewide so crawlers reach the library
// (and its ~2,000 gem pages) from every page on the site.
const FOOTER_CATEGORIES: { href: string; label: string }[] = [
  { href: "/library/c/design-tools-and-generators", label: "Design generators" },
  { href: "/library/c/ui-components-and-libraries", label: "UI components" },
  { href: "/library/c/ai-skills-agents-and-mcp", label: "AI skills & MCP" },
  { href: "/library/c/motion-and-animation", label: "Motion & animation" },
  { href: "/library/c/shaders-webgl-and-creative-coding", label: "Shaders & WebGL" },
  { href: "/library/c/clonable-projects-and-starters", label: "Starters to clone" },
];

export function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer
      aria-labelledby="footer-h"
      data-ink="true"
      className="relative bg-foreground text-background"
    >
      <h2 id="footer-h" className="sr-only">the Vibing Company®</h2>

      <div
        className="mx-auto max-w-[1400px] px-6 md:px-10 pt-20 md:pt-28 pb-10"
        style={{ paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))" }}
      >
        <div className="grid md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand */}
          <div className="md:col-span-5 space-y-7">
            <Link to="/" aria-label="the Vibing Company home" className="inline-block">
              <img src={logo.url} alt="the Vibing Company®" width={192} height={48} loading="lazy" decoding="async" className="h-12 w-auto" />
            </Link>
            <p className="font-display text-3xl md:text-5xl leading-[1.02] tracking-[-0.02em] max-w-[14ch]">
              Est. for tomorrow.
            </p>
            <a
              href="mailto:jon@vibing.inc"
              className="inline-block text-base border-b border-background/30 hover:border-background transition"
            >
              jon@vibing.inc
            </a>
          </div>

          {/* Columns */}
          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FooterCol label="Navigate">
              <FLink href="/">Home</FLink>
              <FLinkTo to="/services">Services</FLinkTo>
              <FLink href="/#about">About</FLink>
              <FLinkTo to="/hiring">Hiring</FLinkTo>
              <FLinkTo to="/contact">Contact</FLinkTo>
              <FLink href="https://cal.com/vibinc/30min" external>
                <span className="inline-flex items-center gap-2">
                  <img src={jonPhoto.url} alt="" width={16} height={16} loading="lazy" decoding="async" className="h-4 w-4 rounded-full object-cover" />
                  Talk to Jon
                </span>
              </FLink>
            </FooterCol>
            <FooterCol label="Library">
              <FLinkTo to="/library">The Vibing Library</FLinkTo>
              <FLinkTo to="/library/new">New this week</FLinkTo>
              <FLinkTo to="/library/best">Best-of round-ups</FLinkTo>
              <FLinkTo to="/library/creators">Creators</FLinkTo>
              <FLinkTo to="/library/submit">Submit a project</FLinkTo>
              <FLinkTo to="/library/badge">Featured badge</FLinkTo>
              {FOOTER_CATEGORIES.map((c) => (
                <FLink key={c.href} href={c.href}>{c.label}</FLink>
              ))}
            </FooterCol>
            <FooterCol label="Guides">
              <FLinkTo to="/guides">All guides</FLinkTo>
              <FLink href="/guides/what-is-vibe-coding">What is vibe coding</FLink>
              <FLink href="/guides/lovable-vs-cursor">Lovable vs Cursor</FLink>
              <FLink href="/guides/how-to-build-an-mcp-server-with-lovable">Build an MCP server</FLink>
              <FLink href="/guides/export-data-from-lovable-cloud">Export Lovable Cloud data</FLink>
              <FLink href="/guides/ios-safari-white-bar-fix">iOS Safari white bar fix</FLink>
            </FooterCol>
            <FooterCol label="Elsewhere">
              <FLinkTo to="/easy-lover">Easy Lover extension</FLinkTo>
              <FLinkTo to="/docs">Developer docs &amp; API</FLinkTo>
              <FLinkTo to="/for-agents">For agents</FLinkTo>
              <FLink href="/llms.txt">llms.txt</FLink>
              <FLink href="/api/public/openapi.json">OpenAPI</FLink>
              <FLink href="/.well-known/mcp.json">MCP manifest</FLink>
              <FLink href="https://x.com/thevibingco" external>X</FLink>
              <FLinkTo to="/terms">Terms</FLinkTo>
              <FLinkTo to="/refunds">Refunds</FLinkTo>
              <FLinkTo to="/privacy">Privacy</FLinkTo>
              <FLinkTo to="/cookies">Cookies</FLinkTo>
              <FLinkTo to="/imprint">Imprint</FLinkTo>
            </FooterCol>
          </div>

        </div>

        <div className="mt-16 pt-6 border-t border-background/10 text-sm text-background/60">
          © {year} the Vibing Company®
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-background/50">{label}</p>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function FLink({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) {
  return (
    <li>
      <a
        href={href}
        {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
        className="text-sm text-background/85 hover:text-background transition"
      >
        {children}
      </a>
    </li>
  );
}

function FLinkTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-sm text-background/85 hover:text-background transition">
        {children}
      </Link>
    </li>
  );
}
